"use client";

import { ChevronDownIcon } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

const PORTAL_SELECT_CONTENT_SELECTOR = "[data-portal-select-content]";

export const isPortalSelectContent = (target: EventTarget | null) =>
  target instanceof Element &&
  Boolean(target.closest(PORTAL_SELECT_CONTENT_SELECTOR));

interface SelectOption {
  value: string;
  label: string;
}

interface PortalSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  "aria-label"?: string;
  usePortal?: boolean;
}

export const PortalSelect = React.forwardRef<
  HTMLButtonElement,
  PortalSelectProps
>(
  (
    {
      options,
      value,
      onChange,
      placeholder = "Select an option",
      disabled = false,
      className,
      triggerClassName,
      contentClassName,
      itemClassName,
      "aria-label": ariaLabel,
      usePortal = true,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    // 現在の値に対応するインデックスを取得
    useEffect(() => {
      const index = options.findIndex((option) => option.value === value);
      setSelectedIndex(index);
    }, [value, options]);

    // ポジション計算
    const calculatePosition = useCallback(() => {
      if (!containerRef.current || !usePortal) return;

      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = Math.min(options.length * 40 + 16, 200); // 概算の高さ

      let top = rect.bottom + window.scrollY;

      // 下に表示するスペースがない場合は上に表示
      if (rect.bottom + dropdownHeight > viewportHeight) {
        top = rect.top + window.scrollY - dropdownHeight;
      }

      setPosition({
        top,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }, [options.length, usePortal]);

    // クリック外でドロップダウンを閉じる
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;

        // コンテナ内のクリックは無視
        if (containerRef.current && containerRef.current.contains(target)) {
          return;
        }

        // Portal使用時はコンテンツ内のクリックも無視
        if (
          usePortal &&
          contentRef.current &&
          contentRef.current.contains(target)
        ) {
          return;
        }

        setIsOpen(false);
        setFocusedIndex(-1);
      };

      const handleScroll = () => {
        if (isOpen && usePortal) {
          calculatePosition();
        }
      };

      const handleResize = () => {
        if (isOpen && usePortal) {
          calculatePosition();
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        if (usePortal) {
          window.addEventListener("scroll", handleScroll, true);
          window.addEventListener("resize", handleResize);
        }
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        if (usePortal) {
          window.removeEventListener("scroll", handleScroll, true);
          window.removeEventListener("resize", handleResize);
        }
      };
    }, [isOpen, usePortal, calculatePosition]);

    // キーボードナビゲーション
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (disabled) return;

        switch (event.key) {
          case "Enter":
          case " ":
            event.preventDefault();
            if (isOpen) {
              if (focusedIndex >= 0) {
                const selectedValue = options[focusedIndex]?.value;
                if (!selectedValue) break;
                setIsOpen(false);
                setFocusedIndex(-1);
                requestAnimationFrame(() => {
                  onChange(selectedValue);
                });
              }
            } else {
              calculatePosition();
              setIsOpen(true);
              setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
            }
            break;

          case "Escape":
            event.preventDefault();
            setIsOpen(false);
            setFocusedIndex(-1);
            break;

          case "ArrowDown":
            event.preventDefault();
            if (!isOpen) {
              calculatePosition();
              setIsOpen(true);
              setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
            } else {
              setFocusedIndex((prev) => {
                const nextIndex = prev < options.length - 1 ? prev + 1 : 0;
                return nextIndex;
              });
            }
            break;

          case "ArrowUp":
            event.preventDefault();
            if (!isOpen) {
              calculatePosition();
              setIsOpen(true);
              setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
            } else {
              setFocusedIndex((prev) => {
                const nextIndex = prev > 0 ? prev - 1 : options.length - 1;
                return nextIndex;
              });
            }
            break;

          case "Home":
            event.preventDefault();
            if (isOpen) {
              setFocusedIndex(0);
            }
            break;

          case "End":
            event.preventDefault();
            if (isOpen) {
              setFocusedIndex(options.length - 1);
            }
            break;

          default:
            // 文字キーでの検索
            if (event.key.length === 1 && isOpen) {
              const char = event.key.toLowerCase();
              const currentIndex = focusedIndex >= 0 ? focusedIndex : 0;

              // 現在のインデックスの次から検索
              for (let i = currentIndex + 1; i < options.length; i++) {
                if (options[i]?.label.toLowerCase().startsWith(char)) {
                  setFocusedIndex(i);
                  return;
                }
              }

              // 最初から現在のインデックスまで検索
              for (let i = 0; i <= currentIndex; i++) {
                if (options[i]?.label.toLowerCase().startsWith(char)) {
                  setFocusedIndex(i);
                  return;
                }
              }
            }
            break;
        }
      },
      [
        disabled,
        isOpen,
        focusedIndex,
        selectedIndex,
        options,
        onChange,
        calculatePosition,
      ],
    );

    // フォーカスされたアイテムがビューポートに入るようにスクロール
    useEffect(() => {
      if (isOpen && focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
        itemRefs.current[focusedIndex]!.scrollIntoView({
          block: "nearest",
        });
      }
    }, [isOpen, focusedIndex]);

    const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;

      if (!isOpen) {
        calculatePosition();
        setIsOpen(true);
        setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
      } else {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    const handleOptionClick = (optionValue: string) => {
      setIsOpen(false);
      setFocusedIndex(-1);
      // 状態更新後にonChangeを呼び出す
      requestAnimationFrame(() => {
        onChange(optionValue);
      });
    };

    const selectedOption = options.find((option) => option.value === value);

    return (
      <div ref={containerRef} className={cn("relative", className)}>
        <button
          ref={ref}
          type="button"
          className={cn(
            "relative w-full cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-left transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            "focus:border-ring focus:ring-ring/50 focus:ring-[3px]",
            "disabled:cursor-not-allowed disabled:opacity-50",
            triggerClassName,
            // triggerClassNameの後に適用して優先度を上げる
            "focus:outline-none",
            isOpen && "ring-2 ring-ring/20 ring-offset-0",
          )}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onClick={handleTriggerClick}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={ariaLabel}
          aria-describedby={isOpen ? "select-listbox" : undefined}
        >
          <span className="block truncate">
            {selectedOption?.label || placeholder}
          </span>
          <ChevronDownIcon
            className={cn(
              "absolute right-1 top-1/2 h-4 w-4 -translate-y-1/2 transform transition-transform",
              isOpen && "rotate-180",
            )}
          />
        </button>

        {isOpen &&
          usePortal &&
          typeof window !== "undefined" &&
          createPortal(
            <div
              ref={contentRef}
              data-portal-select-content
              data-state="open"
              className={cn(
                "fixed z-[100] max-h-60 overflow-y-auto rounded-md border border-input bg-popover shadow-md",
                "animate-in fade-in-0 zoom-in-95",
                contentClassName,
              )}
              style={{
                top: position.top,
                left: position.left,
                width: position.width,
              }}
              role="listbox"
              id="select-listbox"
              aria-label={ariaLabel}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
            >
              <div className="py-1">
                {options.map((option, index) => (
                  <div
                    key={`portal-option-${index}-${option.value}`}
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                    className={cn(
                      "relative cursor-pointer select-none px-3 py-2 text-sm transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      focusedIndex === index &&
                        "bg-accent text-accent-foreground",
                      selectedIndex === index && "bg-muted font-medium",
                      itemClassName,
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleOptionClick(option.value);
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleOptionClick(option.value);
                    }}
                    onMouseEnter={() => setFocusedIndex(index)}
                    role="option"
                    aria-selected={selectedIndex === index}
                  >
                    <span
                      className="block leading-tight max-h-10 overflow-hidden"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {option.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>,
            document.body,
          )}

        {isOpen && !usePortal && (
          <div
            ref={contentRef}
            className={cn(
              "absolute z-50 mt-1 w-full rounded-md border border-input bg-popover",
              "animate-in fade-in-0 zoom-in-95",
              contentClassName,
            )}
            role="listbox"
            id="select-listbox"
            aria-label={ariaLabel}
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="py-1">
              {options.map((option, index) => (
                <div
                  key={`option-${index}-${option.value}`}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  className={cn(
                    "relative cursor-pointer select-none px-3 py-2 text-sm transition-colors",
                    "hover:bg-accent hover:text-accent-foreground",
                    focusedIndex === index &&
                      "bg-accent text-accent-foreground",
                    selectedIndex === index && "bg-muted font-medium",
                    itemClassName,
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOptionClick(option.value);
                  }}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOptionClick(option.value);
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                  role="option"
                  aria-selected={selectedIndex === index}
                >
                  <span
                    className="block leading-tight max-h-10 overflow-hidden"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {option.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  },
);

PortalSelect.displayName = "PortalSelect";
